import React, { useState, useEffect, useCallback } from 'react';
import { BookOpen, Edit, Trash2, ArrowLeft, Upload, Image as ImageIcon } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogTab({ auth }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingPost, setEditingPost] = useState(null);

  const loadPosts = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/blog`, { headers: { 'Authorization': `Basic ${auth}` } });
      if (res.ok) setPosts(await res.json());
    } catch { /* ignore */ }
    setLoading(false);
  }, [auth]);

  useEffect(() => { loadPosts(); }, [loadPosts]);

  const deletePost = async (postId) => {
    if (!window.confirm('Er du sikker på at du vil slette dette innlegget?')) return;
    try {
      await fetch(`${API}/admin/blog/${postId}`, { method: 'DELETE', headers: { 'Authorization': `Basic ${auth}` } });
      loadPosts();
    } catch { /* ignore */ }
  };

  if (showEditor) {
    return <BlogEditor auth={auth} post={editingPost} onBack={() => { setShowEditor(false); setEditingPost(null); }} onSuccess={() => { setShowEditor(false); setEditingPost(null); loadPosts(); }} />;
  }

  if (loading) return <div className="text-stone-500">Laster...</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-semibold text-stone-900">Blogg ({posts.length} innlegg)</h2>
        <button onClick={() => setShowEditor(true)} className="flex items-center gap-2 bg-emerald-800 text-white hover:bg-emerald-900 rounded-lg px-4 py-2 text-sm font-medium transition-colors" data-testid="new-blog-post-btn">
          <BookOpen size={16} /> Nytt innlegg
        </button>
      </div>

      {posts.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-stone-200 p-12 text-center">
          <BookOpen className="mx-auto text-stone-300 mb-4" size={48} />
          <p className="text-stone-500">Ingen blogginnlegg ennå. Skriv ditt første!</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="admin-blog-list">
          {posts.map((p) => (
            <div key={p.id} className="bg-white rounded-xl shadow-sm border border-stone-200 overflow-hidden">
              {p.cover_image && (
                <div className="w-full h-32 sm:h-40">
                  <img src={p.cover_image} alt="" className="w-full h-full object-cover" />
                </div>
              )}
              <div className="p-4 sm:p-5">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className={`text-xs font-semibold px-2 py-1 rounded-full ${p.published ? 'bg-emerald-100 text-emerald-800' : 'bg-stone-100 text-stone-600'}`}>
                    {p.published ? 'Publisert' : 'Kladd'}
                  </span>
                  {p.category && <span className="text-xs font-medium px-2 py-1 rounded-full bg-blue-50 text-blue-700">{p.category}</span>}
                  {p.published_date && <span className="text-xs text-stone-400">{new Date(p.published_date).toLocaleDateString('no-NO')}</span>}
                </div>
                <h3 className="font-semibold text-stone-900 text-base sm:text-lg">{p.title}</h3>
                <p className="text-sm text-stone-500 mt-1 line-clamp-2">{p.excerpt}</p>
                <div className="flex gap-2 mt-3 pt-3 border-t border-stone-100">
                  <button onClick={() => { setEditingPost(p); setShowEditor(true); }} className="flex-1 sm:flex-none text-xs bg-stone-100 hover:bg-stone-200 text-stone-700 px-4 py-2.5 sm:py-1.5 rounded-lg font-medium transition-colors flex items-center justify-center gap-1" data-testid={`edit-blog-${p.id}`}>
                    <Edit size={14} /> Rediger
                  </button>
                  <button onClick={() => deletePost(p.id)} className="text-xs bg-red-50 hover:bg-red-100 text-red-600 px-4 py-2.5 sm:py-1.5 rounded-lg font-medium transition-colors flex items-center justify-center gap-1" data-testid={`delete-blog-${p.id}`}>
                    <Trash2 size={14} /> Slett
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function BlogEditor({ auth, post, onBack, onSuccess }) {
  const [title, setTitle] = useState(post?.title || '');
  const [content, setContent] = useState(post?.content || '');
  const [excerpt, setExcerpt] = useState(post?.excerpt || '');
  const [category, setCategory] = useState(post?.category || '');
  const [coverPreview, setCoverPreview] = useState(post?.cover_image || null);
  const [coverBase64, setCoverBase64] = useState(null);
  const [published, setPublished] = useState(post?.published || false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [uploadingInline, setUploadingInline] = useState(false);

  const CATEGORIES = ['Gravsteinspleie', 'DC-Protect', 'Tips og råd', 'Nyheter', 'Gjør-det-selv'];

  const handleCoverChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setCoverBase64(reader.result);
      setCoverPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const removeCover = () => {
    setCoverBase64(null);
    setCoverPreview(null);
  };

  const handleInlineImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingInline(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const res = await fetch(`${API}/admin/blog/image`, {
          method: 'POST',
          headers: { 'Authorization': `Basic ${auth}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ image: reader.result })
        });
        if (res.ok) {
          const data = await res.json();
          const imgTag = `<img src="${data.url}" alt="Bilde" style="max-width:100%;border-radius:12px;margin:16px 0" />`;
          setContent(prev => prev + '\n' + imgTag);
          setMessage('Bilde lastet opp og satt inn i innholdet!');
          setTimeout(() => setMessage(''), 3000);
        }
      } catch { /* ignore */ }
      setUploadingInline(false);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage('');
    const body = {
      title, content, excerpt, published, category,
      cover_image: coverBase64 || (coverPreview ? coverPreview : null)
    };
    try {
      const url = post ? `${API}/admin/blog/${post.id}` : `${API}/admin/blog`;
      const method = post ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Authorization': `Basic ${auth}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (res.ok) {
        setMessage(post ? 'Innlegg oppdatert!' : 'Innlegg opprettet!');
        setTimeout(() => onSuccess(), 800);
      } else {
        const err = await res.json();
        setMessage(err.detail || 'Kunne ikke lagre blogginnlegget.');
      }
    } catch { setMessage('Kunne ikke koble til serveren.'); }
    finally { setIsSubmitting(false); }
  };

  return (
    <div className="pb-20 sm:pb-0">
      <button onClick={onBack} className="flex items-center gap-2 text-stone-600 hover:text-stone-900 mb-4 py-2">
        <ArrowLeft size={18} /> Tilbake
      </button>
      <h2 className="text-xl sm:text-2xl font-semibold text-stone-900 mb-4 sm:mb-6">{post ? 'Rediger innlegg' : 'Nytt blogginnlegg'}</h2>
      <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-stone-200 p-4 sm:p-8 space-y-5" data-testid="blog-editor-form">
        {/* Cover image */}
        <div>
          <label className="block text-sm font-medium text-stone-700 mb-2">Coverbilde</label>
          {coverPreview ? (
            <div className="relative rounded-xl overflow-hidden border border-stone-200 mb-2">
              <img src={coverPreview} alt="Cover" className="w-full h-40 sm:h-48 object-cover" />
              <button type="button" onClick={removeCover} className="absolute top-2 right-2 bg-red-600 text-white p-2 rounded-full hover:bg-red-700 transition-colors" data-testid="blog-remove-cover-btn">
                <Trash2 size={16} />
              </button>
            </div>
          ) : (
            <label className="flex flex-col items-center gap-2 border-2 border-dashed border-stone-300 rounded-xl p-6 sm:p-8 cursor-pointer hover:border-emerald-700 active:border-emerald-800 active:bg-emerald-50 transition-colors">
              <Upload className="text-stone-400" size={28} />
              <span className="text-sm text-stone-500 text-center">Trykk for å laste opp coverbilde</span>
              <span className="text-xs text-stone-400">PNG, JPG</span>
              <input type="file" accept="image/*" onChange={handleCoverChange} className="hidden" data-testid="blog-cover-input" />
            </label>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-stone-700 mb-2">Tittel *</label>
          <input type="text" required value={title} onChange={(e) => setTitle(e.target.value)} className="w-full px-3 sm:px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent text-base sm:text-lg font-semibold" placeholder="Skriv en fengende tittel..." data-testid="blog-title-input" />
        </div>

        {/* Category */}
        <div>
          <label className="block text-sm font-medium text-stone-700 mb-2">Kategori</label>
          <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-2">
            {CATEGORIES.map(c => (
              <button key={c} type="button" onClick={() => setCategory(c)}
                className={`px-3 py-2 sm:py-1.5 rounded-full text-xs font-medium transition-all ${category === c ? 'bg-emerald-800 text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200 active:bg-stone-300'}`}
                data-testid={`blog-cat-${c}`}
              >
                {c}
              </button>
            ))}
          </div>
          <input type="text" value={category} onChange={(e) => setCategory(e.target.value)} className="w-full px-3 sm:px-4 py-2.5 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent text-sm" placeholder="Eller skriv inn egen kategori..." data-testid="blog-category-input" />
        </div>

        <div>
          <label className="block text-sm font-medium text-stone-700 mb-2">Kort beskrivelse</label>
          <input type="text" value={excerpt} onChange={(e) => setExcerpt(e.target.value)} className="w-full px-3 sm:px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent text-sm sm:text-base" placeholder="En kort oppsummering som vises i listen..." data-testid="blog-excerpt-input" />
        </div>
        <div>
          <div className="flex items-center justify-between mb-2 gap-2">
            <label className="block text-sm font-medium text-stone-700">Innhold *</label>
            <label className={`flex items-center gap-1.5 text-xs font-medium cursor-pointer px-3 py-2 sm:py-1.5 rounded-lg transition-colors whitespace-nowrap ${uploadingInline ? 'bg-stone-100 text-stone-400' : 'bg-emerald-50 text-emerald-800 hover:bg-emerald-100 active:bg-emerald-200'}`}>
              <ImageIcon size={14} />
              {uploadingInline ? 'Laster opp...' : 'Sett inn bilde'}
              <input type="file" accept="image/*" onChange={handleInlineImageUpload} className="hidden" disabled={uploadingInline} data-testid="blog-inline-image-input" />
            </label>
          </div>
          <textarea value={content} onChange={(e) => setContent(e.target.value)} className="w-full px-3 sm:px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent text-sm leading-relaxed" rows={12} placeholder={"<h2>Overskrift</h2>\n<p>Skriv ditt innlegg her...</p>\n\n<h3>Underoverskrift</h3>\n<p>Mer tekst...</p>"} data-testid="blog-content-input" />
          <p className="text-xs text-stone-400 mt-1">HTML-tagger: h2, h3, p, ul, li, strong, blockquote</p>
        </div>
        <label className="flex items-center gap-3 cursor-pointer py-1">
          <input type="checkbox" checked={published} onChange={(e) => setPublished(e.target.checked)} className="w-6 h-6 rounded border-stone-300 text-emerald-800 focus:ring-emerald-800" data-testid="blog-publish-checkbox" />
          <span className="text-sm text-stone-700">Publiser innlegget</span>
        </label>

        {message && <p className={`text-center text-sm ${message.includes('oppdatert') || message.includes('opprettet') || message.includes('lastet opp') ? 'text-emerald-600' : 'text-red-600'}`}>{message}</p>}

        {/* Sticky submit on mobile */}
        <div className="hidden sm:block">
          <button type="submit" disabled={isSubmitting} className="w-full bg-emerald-800 text-white hover:bg-emerald-900 rounded-lg px-6 py-3 font-medium transition-colors disabled:opacity-50" data-testid="blog-submit-btn">
            {isSubmitting ? 'Lagrer...' : (post ? 'Oppdater innlegg' : 'Opprett innlegg')}
          </button>
        </div>
      </form>

      {/* Sticky bottom bar on mobile */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-stone-200 p-3 sm:hidden z-50" data-testid="blog-mobile-submit-bar">
        <button type="button" onClick={(e) => { e.preventDefault(); document.querySelector('[data-testid="blog-editor-form"]')?.requestSubmit(); }} disabled={isSubmitting} className="w-full bg-emerald-800 text-white hover:bg-emerald-900 rounded-lg px-6 py-3.5 font-medium transition-colors disabled:opacity-50 text-base">
          {isSubmitting ? 'Lagrer...' : (post ? 'Oppdater innlegg' : 'Opprett innlegg')}
        </button>
      </div>
    </div>
  );
}
